import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UsersIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { groupOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from '../components/NotificationManager';
import GroupOrderDetails from '../components/GroupOrderDetails';
import GroupOrderCard from '../components/GroupOrderCard';
import LoadingPage from '../components/Loader';

const GroupOrdersPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Get current user info
    const userData = localStorage.getItem('byteUser');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setCurrentUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await groupOrdersApi.getGroupOrderDetails(orderId);
      if (response.success) {
        setOrderDetails(response.data);
      } else {
        setError(response.message || 'Group order not found');
      }
    } catch (err) {
      console.error('Failed to fetch group order details:', err);
      setError('Failed to load group order');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinOrder = async () => {
    if (!currentUser) {
      showErrorNotification('Please log in to join this group order');
      navigate('/login');
      return;
    }

    try {
      const response = await groupOrdersApi.joinGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Successfully joined group order!');
        fetchOrderDetails();
        setShowDetails(true);
      } else {
        showErrorNotification(response.message || 'Failed to join group order');
      }
    } catch (error) {
      showErrorNotification('Failed to join group order');
    }
  };

  const handlePayOrder = async () => {
    try {
      const response = await groupOrdersApi.payForGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Payment processed successfully!');
        fetchOrderDetails();
      } else {
        showErrorNotification(response.message || 'Payment failed');
      }
    } catch (error) {
      showErrorNotification('Payment failed');
    }
  };

  const isParticipant = () => {
    if (!currentUser || !orderDetails?.participants) return false;
    return orderDetails.participants.some(p => 
      p.user._id === currentUser._id || p.user.username === currentUser.username
    );
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-8 text-center"
          >
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-crust font-display mb-2">
              Group Order Not Found
            </h2>
            <p className="text-olive-light font-sans mb-6">
              {error}
            </p>
            <div className="flex gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-6 py-3 border-2 border-secondary-300 text-olive-light rounded-xl font-bold font-display hover:bg-secondary-50 transition-all duration-300"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Go Back
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/user/advanced-orders')}
                className="px-6 py-3 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Browse Group Orders
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl border-2 border-secondary-300 text-olive-light hover:bg-secondary-50 transition-all duration-300"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </motion.button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-crust font-display flex items-center gap-3">
              <UsersIcon className="text-pepperoni" />
              Group Order
            </h1>
            <p className="text-gray-600 font-sans">Join and order together</p>
          </div>
        </motion.div>

        {/* Group Order Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <GroupOrderCard
            order={orderDetails}
            onJoin={handleJoinOrder}
            onView={() => setShowDetails(true)}
            onPay={handlePayOrder}
            currentUser={currentUser}
            isUserOrder={isParticipant()}
          />
        </motion.div>

        {/* Call to Action */}
        {!currentUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-pepperoni/10 to-primary-800/10 rounded-2xl p-6 border-2 border-pepperoni/20 text-center"
          >
            <h3 className="text-xl font-bold text-crust font-display mb-2">
              Ready to Join?
            </h3>
            <p className="text-olive-light font-sans mb-4">
              Log in to join this group order and start adding your meals
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite px-8 py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Log In to Join
            </motion.button>
          </motion.div>
        )}

        {/* Detailed View Modal */}
        {showDetails && (
          <GroupOrderDetails
            orderId={orderId}
            isOpen={showDetails}
            onClose={() => setShowDetails(false)}
            onUpdate={fetchOrderDetails}
          />
        )}
      </div>
    </div>
  );
};

export default GroupOrdersPage;